/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';

import Axios, { AxiosResponse } from 'axios';

import { ApiError, BadRequest, instance } from '@/apis';

const exceptionPhoneNumber = ['01077568431'];

export const useCertNumber = () => {
  const [creatingCertNumber, setCreatingCertNumber] = useState<boolean>(false);
  const [created, setCreated] = useState<boolean>(false);

  const [verifyingCertNumber, setVerifyingCertNumber] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);

  const [checkingCertNumber, setCheckingCertNumber] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(false);

  const resetCertNumber = () => {
    setCreatingCertNumber(false);
    setCreated(false);
    setVerifyingCertNumber(false);
    setVerified(false);
    setCheckingCertNumber(false);
    setChecked(false);
  };

  const createCertNumber = async (phone: string) => {
    if (phone === '') {
      throw new BadRequest('휴대폰 번호를 입력해주세요');
    }

    if (!(phone.length === 10 || phone.length === 11)) {
      throw new BadRequest('10자리 혹은 11자리 휴대폰 번호를 입력해주세요');
    }

    const payload = {
      phone,
    };

    try {
      setCreatingCertNumber(true);
      await instance.post(`/cert-number`, payload, {
        validateStatus: function (status: number) {
          return status === 201;
        },
      });

      setCreated(true);
    } catch (error) {
      setCreated(false);
      if (Axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          const regex = /[^0-9]/g;
          const detail = error.response?.data?.detail;
          const count = detail && detail?.replace(regex, '');

          setCreated(true);
          throw new Error(`${count || '수십'}초 후에 다시 시도해주세요.`);
        }

        if (error.response?.status === 200) {
          throw new Error('일시적인 오류입니다. 잠시 후 다시 시도해주세요.');
        }

        if (error.response?.status === 400) {
          if (error.response?.data.message === 'phone is required.') {
            throw new BadRequest('휴대폰 번호를 확인하세요');
          }

          if (error.response?.data.message === 'invalid phone length') {
            throw new BadRequest('10자리 혹은 11자리 휴대폰 번호를 입력하세요');
          } else {
            throw new BadRequest(error.response?.data.message);
          }
        }

        throw new ApiError(error.message);
      }

      throw new Error();
    } finally {
      setCreatingCertNumber(false);
    }
  };

  const verifyCertNumber = async (
    phone: string,
    certNumber: string,
    customerUniqueCheck?: boolean,
    isAnnonymous?: boolean,
  ) => {
    if (phone === '') {
      throw new BadRequest('휴대폰 번호를 입력해주세요');
    }

    if (certNumber === '') {
      throw new BadRequest('인증번호를 입력해주세요');
    }

    if (certNumber.length !== 4) {
      throw new BadRequest('4자리 인증번호를 입력해주세요');
    }

    const responseInterceptor = instance.interceptors.response.use(function (
      response: AxiosResponse,
    ) {
      if (response.status === 200) {
        const { data } = response;
        if (data.message === 'phone, cert_number is required.') {
          return Promise.reject(new BadRequest('전화번호와 인증번호를 확인하세요'));
        } else if (data.message === 'invalid or expired certification number') {
          return Promise.reject(
            new BadRequest('인증번호가 만료되었습니다. 인증번호를 재발급 받으세요.'),
          );
        } else if (data.message === 'certification number is incorrect') {
          return Promise.reject(new BadRequest('인증번호가 일치하지 않습니다'));
        } else if (data.message === 'certification number is expired') {
          return Promise.reject(new BadRequest('인증번호가 만료되었습니다. 재발급 받아주세요.'));
        } else if (data.message === 'not unique phone number') {
          return Promise.reject(new BadRequest('해당 번호로 이미 가입하셨습니다.'));
        }
      }

      return response;
    });

    const payload = {
      phone,
      cert_number: certNumber,
    };

    if (!exceptionPhoneNumber.includes(phone)) {
      customerUniqueCheck && Object.assign(payload, { customer_unique_check: customerUniqueCheck });
    }

    if (isAnnonymous) {
      Object.assign(payload, { need_temp_token: true, user_type: 'annonymous_customer' });
    }

    try {
      setVerifyingCertNumber(true);
      const verifyCertNumberResponse = await instance.post<
        any,
        { data: { code?: string; messgage: string; temp_token: string } }
      >('/verify-cert-number', payload);
      setVerified(true);

      return { token: verifyCertNumberResponse.data.temp_token ?? '' };
    } catch (error) {
      setVerified(false);

      if (Axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          if (error.response?.data.message === 'invalid or expired certification number') {
            throw new BadRequest('일치하지 않거나, 만료된 인증번호입니다');
          } else {
            throw new BadRequest(error.response?.data.message);
          }
        }

        throw new ApiError(error.message);
      }

      throw error;
    } finally {
      setVerifyingCertNumber(false);

      instance.interceptors.response.eject(responseInterceptor);
    }
  };

  const checkCertNumber = async (phone: string, certNumber: string) => {
    const responseInterceptor = instance.interceptors.response.use(function (
      response: AxiosResponse,
    ) {
      if (response.status === 200) {
        const { data } = response;
        if (data.message === 'phone, cert_number is required.') {
          return Promise.reject(new BadRequest('전화번호와 인증번호를 확인하세요.'));
        } else if (data.message === 'invalid or expired certification number') {
          return Promise.reject(
            new BadRequest('인증번호가 만료되었습니다. 인증번호를 재발급 받으세요.'),
          );
        } else if (data.message === 'certification number is incorrect') {
          return Promise.reject(new BadRequest('인증번호가 일치하지 않습니다'));
        } else if (data.message === 'certification number is expired') {
          return Promise.reject(new BadRequest('인증번호가 만료되었습니다. 재발급 받아주세요.'));
        }
      }

      return response;
    });

    const payload = {
      phone,
      cert_number: certNumber,
    };
    try {
      setCheckingCertNumber(true);
      await instance.post<any, { data: { code?: string; messgage: string } }>(
        '/register/cert-number',
        payload,
      );
      setChecked(true);
    } catch (error) {
      if (Axios.isAxiosError(error)) {
        if (error.response) {
          throw new BadRequest(error.response.data.message);
        }
      }
      setChecked(false);
      throw error;
    } finally {
      setCheckingCertNumber(false);

      instance.interceptors.response.eject(responseInterceptor);
    }
  };

  return {
    createCertNumber,
    creatingCertNumber,
    created,

    verifyCertNumber,
    verifyingCertNumber,
    verified,

    checkCertNumber,
    checkingCertNumber,
    checked,

    resetCertNumber,
  };
};
